import { StringHelper } from "@swivl-mono/helpers";
import { LocationScope, LocationVarInheritance, MergedLocationVar } from "@swivl-mono/types";
import { Input, PasswordInput, TextArea } from "@swivl-mono/ui";
import React, { useEffect, useMemo } from "react";
import { useAutosave } from "react-autosave";

import { isURLField, isValidURL } from "../../../../Models/Variables/LocationVars.Model";
import { WorksheetComponentLabel } from "../Label/WorksheetComponent.Label";
import "./WorksheetComponent.scss";

interface Props {
  label?: string;
  variable: MergedLocationVar;
  className: string;
  type: LocationScope;
  controlType: "input" | "textarea" | "password";
  isEditing: boolean;
  disableReverting?: boolean;
  saveVariable: (value: string) => Promise<string | undefined>;
  shouldSaveCallback?: (value: string) => Promise<boolean>;
  customize(): void;
  deleteVariable(): void;
  isLoading: boolean;
  tooltip?: string;
}

export function WorksheetComponentText(props: Props) {
  const { label, type, variable, isEditing, disableReverting, saveVariable, deleteVariable } = props;

  const [value, setValue] = React.useState<string>(variable.value);
  const isURL = useMemo(() => isURLField(variable.key), [variable.key]);
  const hasURLError = isURL && value !== "" && !isValidURL(value);

  useEffect(() => {
    setValue(variable.value);
  }, [variable.value]);

  useAutosave({
    data: value,
    onSave: async (v) => {
      if (v !== variable.value) {
        if (props.shouldSaveCallback) {
          const shouldSave = await props.shouldSaveCallback(v);
          if (!shouldSave) {
            setValue(variable.value);
            return;
          }
        }
        const persistedValue = await saveVariable(v);

        if (persistedValue !== undefined && persistedValue !== v) {
          setValue(persistedValue);
        }
      }
    }
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value.replace(/\r?\n/g, "\\n");
    setValue(newValue);
  };

  return (
    <div
      className={`group WorksheetComponent WorksheetComponentText relative ${props.className} ${isEditing ? "isEditing" : ""} inheritance_${variable.inheritance} control_${props.controlType} `}
    >
      <WorksheetComponentLabel
        key={variable.inheritance + variable.key + variable.id}
        text={label}
        inheritance={variable.inheritance}
        isEditing={isEditing}
        type={type}
        canRevert={!disableReverting}
        isLoading={props.isLoading}
        deleteVariable={deleteVariable}
        tooltip={props.tooltip}
      />
      {isEditing ? (
        variable.inheritance === LocationVarInheritance.NONE ? (
          props.controlType === "input" ? (
            <Input
              value={value || ""}
              onChange={handleChange}
              error={hasURLError ? "Please enter a valid URL (e.g. https://example.com)" : undefined}
            />
          ) : props.controlType === "password" ? (
            <PasswordInput value={value || ""} onChange={handleChange} />
          ) : (
            <TextArea value={value.replace(/\\n/g, "\n") || ""} onChange={handleChange} />
          )
        ) : (
          <div className="parentText" onClick={props.customize}>
            {value}
            <div className="parentTextOverlay">Click to Customize</div>
          </div>
        )
      ) : props.controlType === "password" ? (
        <div className="valueText whitespace-pre-wrap">{StringHelper.replaceNewLineWithLineBreak("•".repeat(value.length))}</div>
      ) : (
        <div className="valueText whitespace-pre-wrap">{StringHelper.replaceNewLineWithLineBreak(value)}</div>
      )}
    </div>
  );
}
